import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Input, message, Spin } from "antd";
import {
  Search,
  CreditCard,
  PlusCircle,
  RefreshCw,
  Home,
  CheckCircle,
  AlertCircle,
  Clock,
  Hash,
} from "react-feather";
import axios from "axios";
import KOTGenerationPage from "./KOTGenerationPage";
import BillGeneration from "./components/billing/BillGeneration";

const normalizeArrayResponse = (data, preferredKey) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.[preferredKey])) return data[preferredKey];
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.categories)) return data.categories;
  if (Array.isArray(data?.rooms)) return data.rooms;
  return [];
};

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const money = (value) => {
  const n = toNumber(value);
  return n.toLocaleString("en-IN", {
    minimumFractionDigits: n % 1 ? 2 : 0,
    maximumFractionDigits: 2,
  });
};

const getCurrency = () => localStorage.getItem("currencycode") || "₹";

const getRoomNumber = (room) =>
  String(
    room?.roomNumber ||
      room?.room_number ||
      room?.room_no ||
      room?.room_name ||
      room?.number ||
      room?.id ||
      ""
  );

const getRoomId = (room) =>
  String(room?.id || room?.room_id || room?.roomNumber || room?.room_number || room?.room_no || "");

const getRoomLocation = (room) =>
  room?.floor_name ||
  room?.floor ||
  room?.location ||
  room?.block ||
  room?.room_type ||
  room?.roomType ||
  "Room Service";

const getKotItemsCount = (room) =>
  (Array.isArray(room?.kots) ? room.kots : []).reduce(
    (sum, kot) => sum + (Array.isArray(kot?.items) ? kot.items.length : 0),
    0
  );

const getKotQuantityCount = (room) =>
  (Array.isArray(room?.kots) ? room.kots : []).reduce((sum, kot) => {
    const items = Array.isArray(kot?.items) ? kot.items : [];
    return sum + items.reduce((itemSum, item) => itemSum + toNumber(item?.qty, 0), 0);
  }, 0);

const getRoomPendingAmount = (room) =>
  (Array.isArray(room?.kots) ? room.kots : []).reduce((sum, kot) => {
    const items = Array.isArray(kot?.items) ? kot.items : [];
    const kotAmount = toNumber(kot?.grand_total || kot?.total || kot?.amount, 0);

    if (kotAmount > 0) return sum + kotAmount;

    return (
      sum +
      items.reduce((itemSum, item) => {
        const amount = toNumber(item?.amount, 0);
        if (amount > 0) return itemSum + amount;

        return itemSum + toNumber(item?.qty, 0) * toNumber(item?.price_standard || item?.price, 0);
      }, 0)
    );
  }, 0);

const POS_SELECT_ZINDEX = 2147483647;

const RoomServiceDashboard = () => {
  const location = useLocation();

  const [searchTerm, setSearchTerm] = useState("");
  const [showRunningOrders, setShowRunningOrders] = useState(false);
  const [selectedRoomForBilling, setSelectedRoomForBilling] = useState(null);
  const [isBillingPanelOpen, setIsBillingPanelOpen] = useState(false);
  const [isKOTModalOpen, setIsKOTModalOpen] = useState(false);
  const [currentKOTRoom, setCurrentKOTRoom] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [loadingRooms, setLoadingRooms] = useState(true);
  const [loadingBill, setLoadingBill] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [error, setError] = useState(null);
  const [kotItems, setKotItems] = useState([]);
  const [kotCount, setKotCount] = useState(0);
  const [settings, setSettings] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);

  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3000/api/pos";
  const token = localStorage.getItem("token");
  const currency = getCurrency();

  const normalizeRoom = useCallback((room) => {
    const roomNumber = getRoomNumber(room);
    const kots = Array.isArray(room?.kots) ? room.kots : [];

    return {
      ...room,
      id: getRoomId(room) || roomNumber,
      roomNumber,
      kots,
      status: kots.length > 0 ? "Running" : "Available",
    };
  }, []);

  const fetchSettings = useCallback(async () => {
    setLoadingSettings(true);

    try {
      const response = await axios.get(`${apiUrl}/setting-pos`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setSettings(response.data.settings || {});
    } catch (err) {
      const errorMessage = err.response?.data?.error || "Failed to fetch settings";
      setError(errorMessage);
      message.error(errorMessage);
    } finally {
      setLoadingSettings(false);
    }
  }, [apiUrl, token]);

  const fetchMenuItems = useCallback(async () => {
    try {
      const response = await axios.get(`${apiUrl}/pos/items/listings`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const activeItems = normalizeArrayResponse(response.data, "items")
        .filter((item) => item?.is_active && typeof item === "object" && item.menu_id && item.name)
        .map((item) => ({
          ...item,
          menu_id: item.menu_id,
          name: item.name,
          category_name: item.category_name || "Uncategorized",
          price_standard: toNumber(item.price_standard, 0),
        }));

      setMenuItems(activeItems);
    } catch (err) {
      const errorMessage = err.response?.data?.error || "Failed to fetch menu items";
      setError(errorMessage);
      message.error(errorMessage);
    }
  }, [apiUrl, token]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${apiUrl}/pos/categories/listings`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const activeCategories = normalizeArrayResponse(response.data, "categories").filter(
        (cat) => cat?.is_active && typeof cat === "object" && cat.category_id
      );

      setCategories(activeCategories);
    } catch (err) {
      const errorMessage = err.response?.data?.error || "Failed to fetch categories";
      setError(errorMessage);
      message.error(errorMessage);
    }
  }, [apiUrl, token]);

  const fetchRooms = useCallback(async () => {
    if (!token) {
      setError("No authentication token found");
      setLoadingRooms(false);
      return;
    }

    try {
      setLoadingRooms(true);

      const response = await axios.get(`${apiUrl}/rooms/occupied-rooms`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const normalizedRooms = normalizeArrayResponse(response.data, "rooms").map(normalizeRoom);

      setRooms(normalizedRooms);
      setError(null);
    } catch (err) {
      console.error("Error fetching rooms:", err);
      const errorMessage = err.response?.data?.error || err.message || "Failed to fetch rooms";
      setError(errorMessage);
      setRooms([]);
      message.error(errorMessage);
    } finally {
      setLoadingRooms(false);
    }
  }, [apiUrl, token, normalizeRoom]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    fetchMenuItems();
  }, [fetchMenuItems]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    fetchRooms();
  }, [fetchRooms, location]);

  const paymentOptions = useMemo(() => {
    const defaultOptions = [
      { value: "Cash", label: "Cash" },
      { value: "UPI", label: "UPI" },
      { value: "Card", label: "Card" },
      { value: "Due", label: "Due" },
    ];

    const settingsPayments =
      settings?.payment_types?.map((pt) => ({
        value: pt.type,
        label: pt.type,
      })) || [];

    return settingsPayments.length > 0 ? settingsPayments : defaultOptions;
  }, [settings]);

  const sourceOptions = useMemo(() => {
    const defaultOptions = [
      { value: "Room Service", label: "Room Service" },
      { value: "Takeaway", label: "Takeaway" },
      { value: "Online", label: "Online" },
    ];

    const settingsSources =
      settings?.rate_card_sources?.map((src) => ({
        value: src.source,
        label: src.source,
      })) || [];

    const seen = new Set();

    return [...defaultOptions, ...settingsSources].filter((option) => {
      const key = String(option.value || "").toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [settings]);

  const roomOptions = useMemo(
    () =>
      rooms.map((room) => ({
        value: getRoomId(room) || getRoomNumber(room),
        label: getRoomNumber(room),
      })),
    [rooms]
  );

  const filteredRooms = useMemo(() => {
    const cleanSearch = String(searchTerm || "").trim().toLowerCase();

    return rooms.filter((room) => {
      const hasRunningKots = Array.isArray(room.kots) && room.kots.length > 0;

      if (showRunningOrders && !hasRunningKots) return false;

      if (!cleanSearch) return true;

      return [
        getRoomNumber(room),
        getRoomLocation(room),
        room?.guest_name,
        room?.reservation_no,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(cleanSearch);
    });
  }, [rooms, searchTerm, showRunningOrders]);

  const occupiedRoomsCount = rooms.length;
  const runningRoomsCount = rooms.filter((room) => Array.isArray(room.kots) && room.kots.length > 0).length;
  const availableRoomsCount = Math.max(0, occupiedRoomsCount - runningRoomsCount);
  const totalKotsCount = rooms.reduce((sum, room) => sum + (Array.isArray(room.kots) ? room.kots.length : 0), 0);
  const totalPendingValue = rooms.reduce((sum, room) => sum + getRoomPendingAmount(room), 0);

  const handleOpenBillingPanel = async (roomId) => {
    setLoadingBill(true);
    setSelectedRoomForBilling(roomId);

    try {
      const response = await axios.get(`${apiUrl}/pos/orders/room/${roomId}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const billData = response.data;

      if (!billData.kots || !Array.isArray(billData.kots)) {
        throw new Error("Invalid bill data structure: 'kots' not found or not an array");
      }

      const itemsWithKOT = billData.kots.flatMap((kot) =>
        (kot.items || []).map((item) => ({
          ...item,
          kot_id: kot.kot_id,
          kot_number: kot.kot_number,
          room_id: kot.room_id || roomId,
          amount: item.amount || toNumber(item.qty, 0) * toNumber(item.price_standard, 0),
          is_taxable: item.is_taxable || false,
          tax_ids: item.tax_ids || [],
          taxes: item.taxes || [],
          total_tax_rate: item.total_tax_rate || 0,
          tax_name: item.tax_name || "",
          tax_rate: item.tax_rate || 0,
          source_prices: item.source_prices || [],
          price_standard: toNumber(item.price_standard, 0),
        }))
      );

      setKotItems(itemsWithKOT);
      setKotCount(billData.kots.length);
      setIsBillingPanelOpen(true);
    } catch (err) {
      console.error("Error fetching bill data:", err);
      message.error("Failed to fetch bill data: " + (err.message || "Unknown error"));
      setKotItems([]);
      setKotCount(0);
      setIsBillingPanelOpen(false);
    } finally {
      setLoadingBill(false);
    }
  };

  const handleCloseBillingPanel = () => {
    setIsBillingPanelOpen(false);
    setSelectedRoomForBilling(null);
    setKotItems([]);
    setKotCount(0);
  };

  const handleOpenKOTModal = (roomId) => {
    setCurrentKOTRoom(roomId);
    setIsKOTModalOpen(true);
  };

  const handleCloseKOTModal = () => {
    setIsKOTModalOpen(false);
    setCurrentKOTRoom(null);
    fetchRooms();
  };

  const addKOT = (tableId, roomId, kot) => {
    setRooms((previousRooms) =>
      previousRooms.map((room) =>
        getRoomNumber(room) === roomId
          ? {
              ...room,
              kots: [...(Array.isArray(room.kots) ? room.kots : []), kot],
              status: "Running",
            }
          : room
      )
    );
  };

  const renderStatusLabel = (room) => {
    const running = Array.isArray(room.kots) && room.kots.length > 0;
    return running ? "Running" : "Ready";
  };

  const renderRoomCard = (room) => {
    const roomNumber = getRoomNumber(room);
    const running = Array.isArray(room.kots) && room.kots.length > 0;
    const pendingAmount = getRoomPendingAmount(room);
    const itemsCount = getKotItemsCount(room);
    const qtyCount = getKotQuantityCount(room);
    const locationLabel = getRoomLocation(room);

    return (
      <article
        key={`${getRoomId(room)}_${roomNumber}`}
        className={`ns-rsd-room-card ${running ? "is-running" : "is-ready"}`}
      >
        <div className="ns-rsd-room-status-row">
          <span className={`ns-rsd-room-status ${running ? "running" : "ready"}`}>
            <span className="ns-rsd-room-dot" />
            {renderStatusLabel(room)}
          </span>

          <span className="ns-rsd-kot-chip">
            {room.kots.length ? `${room.kots.length} KOT` : "No KOT"}
          </span>
        </div>

        <div className="ns-rsd-room-main">
          <div>
            <div className="ns-rsd-room-label">Room</div>
            <h4 title={roomNumber}>{roomNumber}</h4>
          </div>

          {running ? <span className="ns-rsd-live-pulse" aria-label="Running KOT" /> : null}
        </div>

        <div className="ns-rsd-room-location" title={locationLabel}>
          <Home size={13} />
          <span>{locationLabel}</span>
        </div>

        <div className="ns-rsd-room-metrics">
          <span>{itemsCount} items</span>
          <span>{qtyCount} qty</span>
          <span>{running ? `${currency}${money(pendingAmount)}` : "No bill"}</span>
        </div>

        <div className="ns-rsd-room-actions">
          <button
            type="button"
            className="ns-rsd-action-btn primary"
            onClick={() => handleOpenKOTModal(roomNumber)}
            title={`Create KOT for room ${roomNumber}`}
          >
            <PlusCircle size={15} />
            KOT
          </button>

          <button
            type="button"
            className="ns-rsd-action-btn success"
            onClick={() => handleOpenBillingPanel(roomNumber)}
            disabled={!running || (loadingBill && selectedRoomForBilling === roomNumber)}
            title={running ? `Generate bill for room ${roomNumber}` : "No running KOT yet"}
          >
            {loadingBill && selectedRoomForBilling === roomNumber ? <Spin size="small" /> : <CreditCard size={15} />}
            Bill
          </button>
        </div>
      </article>
    );
  };

  const renderLoadingState = () => (
    <div className="ns-rsd-loading">
      <style>{roomServiceStyles}</style>
      <div className="ns-rsd-loading-card">
        <Spin size="large" />
        <strong>Loading room service workspace...</strong>
        <span>Preparing rooms, settings, menu items, and categories.</span>
      </div>
    </div>
  );

  if (loadingRooms || loadingSettings) {
    return renderLoadingState();
  }

  if (error && rooms.length === 0) {
    return (
      <div className="ns-rsd-root">
        <style>{roomServiceStyles}</style>
        <div className="ns-rsd-error">
          <AlertCircle size={34} />
          <h3>Unable to load room service dashboard</h3>
          <p>{error}</p>
          <button type="button" onClick={fetchRooms}>
            <RefreshCw size={16} />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`ns-rsd-root ${isBillingPanelOpen ? "has-billing-panel" : ""}`}>
      <style>{roomServiceStyles}</style>


      {error ? (
        <div className="ns-rsd-soft-error">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      ) : null}

      <section className="ns-rsd-stats-grid">
        <div className="ns-rsd-stat-card accent-blue">
          <span>Occupied Rooms</span>
          <strong>{occupiedRoomsCount}</strong>
          <small>Rooms available for service</small>
        </div>

        <div className="ns-rsd-stat-card accent-amber">
          <span>Running KOTs</span>
          <strong>{totalKotsCount}</strong>
          <small>Open kitchen tickets</small>
        </div>

        <div className="ns-rsd-stat-card accent-green">
          <span>Ready Rooms</span>
          <strong>{availableRoomsCount}</strong>
          <small>No running KOT yet</small>
        </div>

        <div className="ns-rsd-stat-card accent-slate">
          <span>Pending Value</span>
          <strong>{currency}{money(totalPendingValue)}</strong>
          <small>Across visible service rooms</small>
        </div>
      </section>

      <section className="ns-rsd-toolbar">
        <div className="ns-rsd-search-box">
          <Search size={17} />
          <Input
            placeholder="Search room number, floor, block, or guest..."
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            allowClear
            bordered={false}
          />
        </div>

        <div className="ns-rsd-toolbar-right">
          <span className="ns-rsd-showing">
            Showing <strong>{filteredRooms.length}</strong> of <strong>{rooms.length}</strong> rooms
          </span>

          <label className="ns-rsd-toggle-wrap">
            <span>Show Running Orders</span>
            <input
              type="checkbox"
              checked={showRunningOrders}
              onChange={(event) => setShowRunningOrders(event.target.checked)}
            />
            <i />
          </label>

          <button type="button" className="ns-rsd-refresh-btn" onClick={fetchRooms}>
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </section>

      <section className="ns-rsd-room-section">
        <div className="ns-rsd-room-section-head">
          <div>
            <h4>Room Grid</h4>
            <p>Tap KOT to add items. Tap Bill when a room has running KOTs.</p>
          </div>

          <span>
            <Hash size={14} />
            {filteredRooms.length} visible
          </span>
        </div>

        {filteredRooms.length ? (
          <div className="ns-rsd-room-grid">{filteredRooms.map(renderRoomCard)}</div>
        ) : (
          <div className="ns-rsd-empty">
            <Clock size={34} />
            <strong>No rooms found</strong>
            <span>Try clearing the search or turning off the running-orders filter.</span>
          </div>
        )}
      </section>

      {isBillingPanelOpen && (
        <BillGeneration
          isOpen={isBillingPanelOpen}
          onClose={handleCloseBillingPanel}
          entityId={selectedRoomForBilling}
          entityType="room"
          kotItems={kotItems}
          kotCount={kotCount}
          paymentOptions={paymentOptions}
          sourceOptions={sourceOptions}
          settings={settings}
          roomOptions={roomOptions}
          fetchData={fetchRooms}
          apiUrl={apiUrl}
          token={token}
        />
      )}

      {isKOTModalOpen && currentKOTRoom && (
        <KOTGenerationPage
          key={`room-kot-${currentKOTRoom}`}
          roomId={currentKOTRoom}
          onClose={handleCloseKOTModal}
          menuItems={menuItems}
          categories={categories}
          settings={settings}
          tables={[]}
          rooms={rooms}
          setRooms={setRooms}
          addKOT={addKOT}
          calledFrom="room"
        />
      )}
    </div>
  );
};

const roomServiceStyles = `
  .ns-rsd-root {
    min-height: 100%;
    background:
      radial-gradient(circle at 9% 0%, rgba(37, 99, 235, 0.07), transparent 30%),
      radial-gradient(circle at 92% 2%, rgba(34, 197, 94, 0.08), transparent 28%),
      #f8fafc;
    padding: 10px;
    color: #0f172a;
    overflow: visible;
  }

  .ns-rsd-root.has-billing-panel {
    padding-right: min(462px, 37vw);
  }

  .ns-rsd-hero {
    background:
      radial-gradient(circle at top left, rgba(37, 99, 235, 0.13), transparent 34%),
      linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
    border: 1px solid rgba(15, 23, 42, 0.08);
    border-radius: 22px;
    box-shadow: 0 14px 38px rgba(15, 23, 42, 0.075);
    padding: 14px 16px;
    margin-bottom: 10px;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 14px;
    flex-wrap: wrap;
  }

  .ns-rsd-title-wrap {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    min-width: 0;
  }

  .ns-rsd-main-icon {
    width: 46px;
    height: 46px;
    border-radius: 17px;
    background: linear-gradient(135deg, #1d4ed8, #2563eb);
    color: #ffffff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 14px 30px rgba(37, 99, 235, 0.20);
    flex: 0 0 auto;
  }

  .ns-rsd-eyebrow {
    color: #2563eb;
    font-size: 0.7rem;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    font-weight: 950;
    margin-bottom: 5px;
  }

  .ns-rsd-hero h3 {
    color: #001f5b;
    font-size: 1.28rem;
    line-height: 1.16;
    font-weight: 950;
    margin: 0;
  }

  .ns-rsd-hero p {
    color: #64748b;
    font-size: 0.8rem;
    line-height: 1.42;
    font-weight: 760;
    margin: 5px 0 0;
  }

  .ns-rsd-hero-meta {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 7px;
    flex-wrap: wrap;
  }

  .ns-rsd-hero-meta span {
    min-height: 31px;
    border-radius: 999px;
    background: #ffffff;
    color: #334155;
    border: 1px solid rgba(15, 23, 42, 0.08);
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 10px;
    font-size: 0.72rem;
    font-weight: 900;
    white-space: nowrap;
    box-shadow: 0 8px 18px rgba(15, 23, 42, 0.04);
  }

  .ns-rsd-hero-meta span:first-child {
    color: #047857;
    background: #ecfdf5;
    border-color: #bbf7d0;
  }

  .ns-rsd-soft-error {
    background: #fff7ed;
    border: 1px solid #fed7aa;
    color: #9a3412;
    border-radius: 16px;
    padding: 9px 11px;
    margin-bottom: 10px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 0.78rem;
    font-weight: 850;
  }

  .ns-rsd-stats-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 10px;
    margin-bottom: 10px;
  }

  .ns-rsd-stat-card {
    background: #ffffff;
    border: 1px solid rgba(15, 23, 42, 0.08);
    border-radius: 19px;
    box-shadow: 0 12px 30px rgba(15, 23, 42, 0.06);
    padding: 13px 14px;
    border-top: 4px solid #2563eb;
  }

  .ns-rsd-stat-card.accent-amber { border-top-color: #f59e0b; }
  .ns-rsd-stat-card.accent-green { border-top-color: #22c55e; }
  .ns-rsd-stat-card.accent-slate { border-top-color: #64748b; }

  .ns-rsd-stat-card span {
    display: block;
    color: #64748b;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-weight: 950;
  }

  .ns-rsd-stat-card strong {
    color: #0f172a;
    display: block;
    font-size: 1.26rem;
    line-height: 1.18;
    font-weight: 950;
    margin-top: 7px;
  }

  .ns-rsd-stat-card small {
    color: #64748b;
    display: block;
    font-size: 0.74rem;
    line-height: 1.35;
    font-weight: 760;
    margin-top: 5px;
  }

  .ns-rsd-toolbar {
    background: #ffffff;
    border: 1px solid rgba(15, 23, 42, 0.08);
    border-radius: 20px;
    box-shadow: 0 10px 26px rgba(15, 23, 42, 0.055);
    padding: 10px;
    margin-bottom: 10px;
    display: grid;
    grid-template-columns: minmax(260px, 1fr) auto;
    gap: 10px;
    align-items: center;
  }

  .ns-rsd-search-box {
    min-height: 44px;
    background: #f8fafc;
    border: 1px solid rgba(15, 23, 42, 0.10);
    border-radius: 16px;
    padding: 0 12px;
    display: flex;
    align-items: center;
    gap: 8px;
    color: #94a3b8;
  }

  .ns-rsd-search-box .ant-input {
    background: transparent !important;
    font-weight: 760;
    color: #0f172a;
  }

  .ns-rsd-toolbar-right {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 9px;
    flex-wrap: wrap;
  }

  .ns-rsd-showing {
    color: #64748b;
    font-size: 0.75rem;
    font-weight: 840;
    white-space: nowrap;
  }

  .ns-rsd-showing strong { color: #0f172a; }

  .ns-rsd-toggle-wrap {
    min-height: 38px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    cursor: pointer;
    color: #0f172a;
    font-size: 0.76rem;
    font-weight: 900;
    white-space: nowrap;
  }

  .ns-rsd-toggle-wrap input { display: none; }

  .ns-rsd-toggle-wrap i {
    position: relative;
    width: 44px;
    height: 26px;
    border-radius: 999px;
    background: #cbd5e1;
    transition: background 0.16s ease;
    flex: 0 0 auto;
  }

  .ns-rsd-toggle-wrap i::after {
    content: "";
    position: absolute;
    top: 4px;
    left: 4px;
    width: 18px;
    height: 18px;
    border-radius: 999px;
    background: #ffffff;
    box-shadow: 0 3px 8px rgba(15, 23, 42, 0.18);
    transition: transform 0.16s ease;
  }

  .ns-rsd-toggle-wrap input:checked + i { background: #2563eb; }
  .ns-rsd-toggle-wrap input:checked + i::after { transform: translateX(18px); }

  .ns-rsd-refresh-btn {
    min-height: 38px;
    border: 1px solid rgba(15, 23, 42, 0.10);
    background: #ffffff;
    color: #1d4ed8;
    border-radius: 14px;
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 0 12px;
    font-size: 0.76rem;
    font-weight: 950;
    cursor: pointer;
  }

  .ns-rsd-room-section {
    background: #ffffff;
    border: 1px solid rgba(15, 23, 42, 0.08);
    border-radius: 22px;
    box-shadow: 0 14px 38px rgba(15, 23, 42, 0.07);
    overflow: hidden;
  }

  .ns-rsd-room-section-head {
    padding: 13px 15px;
    border-bottom: 1px solid rgba(15, 23, 42, 0.08);
    background:
      radial-gradient(circle at top right, rgba(37, 99, 235, 0.08), transparent 28%),
      #ffffff;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
  }

  .ns-rsd-room-section-head h4 {
    color: #0f172a;
    font-size: 1rem;
    line-height: 1.18;
    font-weight: 950;
    margin: 0;
  }

  .ns-rsd-room-section-head p {
    color: #64748b;
    font-size: 0.76rem;
    line-height: 1.42;
    font-weight: 760;
    margin: 4px 0 0;
  }

  .ns-rsd-room-section-head > span {
    min-height: 30px;
    border-radius: 999px;
    background: #eff6ff;
    color: #1d4ed8;
    border: 1px solid #bfdbfe;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 10px;
    font-size: 0.72rem;
    font-weight: 950;
    white-space: nowrap;
  }

  .ns-rsd-room-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(188px, 1fr));
    gap: 10px;
    padding: 12px;
    align-items: stretch;
  }

  .ns-rsd-room-card {
    min-height: 198px;
    background:
      radial-gradient(circle at top right, rgba(37, 99, 235, 0.05), transparent 34%),
      #ffffff;
    border: 1px solid rgba(15, 23, 42, 0.08);
    border-top: 4px solid #22c55e;
    border-radius: 20px;
    box-shadow: 0 10px 26px rgba(15, 23, 42, 0.06);
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    position: relative;
    overflow: hidden;
    transition: transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease;
  }

  .ns-rsd-room-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 18px 42px rgba(15, 23, 42, 0.11);
  }

  .ns-rsd-room-card.is-running {
    border-top-color: #f59e0b;
    background:
      radial-gradient(circle at top right, rgba(245, 158, 11, 0.10), transparent 32%),
      #ffffff;
  }

  .ns-rsd-room-status-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .ns-rsd-room-status,
  .ns-rsd-kot-chip {
    min-height: 28px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 0 9px;
    font-size: 0.7rem;
    font-weight: 950;
    white-space: nowrap;
  }

  .ns-rsd-room-status.ready { background: #ecfdf5; color: #047857; }
  .ns-rsd-room-status.running { background: #fffbeb; color: #b45309; }

  .ns-rsd-room-dot {
    width: 9px;
    height: 9px;
    border-radius: 999px;
    background: currentColor;
    box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.12);
  }

  .ns-rsd-kot-chip {
    background: #f8fafc;
    color: #475569;
    border: 1px solid rgba(15, 23, 42, 0.08);
  }

  .ns-rsd-room-main {
    min-height: 50px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 9px;
  }

  .ns-rsd-room-label {
    color: #94a3b8;
    font-size: 0.62rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-weight: 950;
    margin-bottom: 2px;
  }

  .ns-rsd-room-main h4 {
    color: #001f5b;
    font-size: 1.58rem;
    line-height: 1;
    font-weight: 950;
    margin: 0;
    max-width: 126px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ns-rsd-live-pulse {
    width: 18px;
    height: 18px;
    border-radius: 999px;
    background: #ef4444;
    box-shadow: 0 0 0 8px rgba(239, 68, 68, 0.12);
    animation: ns-rsd-live-pulse 1.6s ease-in-out infinite;
    flex: 0 0 auto;
  }

  @keyframes ns-rsd-live-pulse {
    0%, 100% { transform: scale(0.94); box-shadow: 0 0 0 5px rgba(239, 68, 68, 0.12); }
    50% { transform: scale(1); box-shadow: 0 0 0 11px rgba(239, 68, 68, 0.05); }
  }

  .ns-rsd-room-location {
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 6px;
    color: #64748b;
    font-size: 0.74rem;
    font-weight: 800;
  }

  .ns-rsd-room-location span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ns-rsd-room-metrics {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  .ns-rsd-room-metrics span {
    min-height: 28px;
    border-radius: 999px;
    background: #f8fafc;
    border: 1px solid rgba(15, 23, 42, 0.08);
    color: #334155;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0 9px;
    font-size: 0.7rem;
    font-weight: 950;
  }

  .ns-rsd-room-actions {
    margin-top: auto;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }

  .ns-rsd-action-btn {
    min-height: 38px;
    border: 0;
    border-radius: 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    font-size: 0.78rem;
    font-weight: 950;
    color: #ffffff;
    cursor: pointer;
    box-shadow: 0 12px 26px rgba(15, 23, 42, 0.10);
    transition: transform 0.16s ease, opacity 0.16s ease;
  }

  .ns-rsd-action-btn:hover:not(:disabled) { transform: translateY(-1px); }
  .ns-rsd-action-btn.primary { background: linear-gradient(135deg, #1d4ed8, #2563eb); }
  .ns-rsd-action-btn.success { background: linear-gradient(135deg, #16a34a, #22c55e); }
  .ns-rsd-action-btn:disabled { opacity: 0.48; cursor: not-allowed; box-shadow: none; }

  .ns-rsd-empty {
    min-height: 320px;
    color: #64748b;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    text-align: center;
    padding: 32px;
  }

  .ns-rsd-empty strong {
    color: #0f172a;
    font-size: 1rem;
    font-weight: 950;
  }

  .ns-rsd-loading {
    min-height: 70vh;
    background: #f8fafc;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
  }

  .ns-rsd-loading-card,
  .ns-rsd-error {
    width: min(460px, 100%);
    background: #ffffff;
    border: 1px solid rgba(15, 23, 42, 0.08);
    border-radius: 24px;
    box-shadow: 0 24px 70px rgba(15, 23, 42, 0.12);
    padding: 28px;
    text-align: center;
    color: #64748b;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
  }

  .ns-rsd-loading-card strong,
  .ns-rsd-error h3 {
    color: #0f172a;
    font-size: 1rem;
    font-weight: 950;
    margin: 0;
  }

  .ns-rsd-error svg { color: #dc2626; }
  .ns-rsd-error p { margin: 0; line-height: 1.45; }

  .ns-rsd-error button {
    min-height: 40px;
    border: 0;
    border-radius: 14px;
    background: #1d4ed8;
    color: #ffffff;
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 0 16px;
    font-weight: 950;
    cursor: pointer;
  }

  .ns-rsd-root .ant-select-dropdown,
  .ns-rsd-root [class*="-menu"] {
    z-index: ${POS_SELECT_ZINDEX} !important;
  }

  @media (max-width: 1320px) {
    .ns-rsd-root.has-billing-panel { padding-right: 10px; }
  }

  @media (max-width: 1100px) {
    .ns-rsd-stats-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .ns-rsd-toolbar { grid-template-columns: 1fr; }
    .ns-rsd-toolbar-right { justify-content: flex-start; }
  }

  @media (max-width: 640px) {
    .ns-rsd-root { padding: 8px; }
    .ns-rsd-stats-grid { grid-template-columns: 1fr; }
    .ns-rsd-room-grid { grid-template-columns: 1fr; }
    .ns-rsd-hero,
    .ns-rsd-room-section-head { flex-direction: column; align-items: stretch; }
    .ns-rsd-hero-meta { justify-content: flex-start; }
  }
`;

export default RoomServiceDashboard;
